import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { useFirestoreNoCompagny, useFirestoreAllIn } from '../../firebase/useFirestore';
import Location from "../../hooks/Location"
import { client } from '../../hooks/Client'
import { isSuperAdminTenant } from '../../utils/superAdminTenant'
import projectDetailTabs from '../../helpers/portfolio/projectDetailTabs'
import { useState, useMemo, useContext, useCallback } from 'react';
import firebase from '../../firebase/compat';
import { Auth } from '../../StateManagment/Auth';
import SelectableGrid from '../../components/common/SelectableGrid';
import { businessDaysSince } from '../../utils/activityAge';
import useRetentionScoresBulk from '../../hooks/retention/useRetentionScoresBulk';
import LastEntriesCard from '../../components/retention/LastEntriesCard.jsx';
import ProjectMetaStrip from '../../components/retention/ProjectMetaStrip.jsx';
import DeccaTriggerButton from '../../components/common/DeccaTriggerButton.jsx';
import describeUnavailableData from '../../helpers/retention/describeUnavailableData.js';
import ProjectNotes from '../../components/portfolio/projectNotes/ProjectNotes.jsx';
import ProjectTasks from '../../components/portfolio/projectTasks/ProjectTasks.jsx';
import ProjectUpdateRequests from '../../components/portfolio/projectUpdateRequests/ProjectUpdateRequests.jsx';
import ProjectMetricsCard from '../../components/portfolio/ProjectMetricsCard.jsx';
import ProjectSectorsCell from '../../components/portfolio/ProjectSectorsCell.jsx';
import SuperAdminSessionModal from '../../components/superAdmin/SuperAdminSessionModal';
import useOverviewCounts from '../../hooks/portfolio/useOverviewCounts';
import useProjectActivityEntries from '../../hooks/portfolio/useProjectActivityEntries';
import useProjectResearchEntries from '../../hooks/portfolio/useProjectResearchEntries';
import useProjectTaskEntries from '../../hooks/portfolio/useProjectTaskEntries';
import useAvailableSectors from '../../hooks/useAvailableSectors';
import { TOC_METRICS, sectorMetricsFor, sectorsWithRegistrations } from '../../helpers/portfolio/projectMetricDefinitions';
import { getSectorInfo } from '../../helpers/meetstandaard/sectorCatalog';
import isFlagOn from '../../helpers/isFlagOn';

// Cron schrijft Author='system' bij auto-aangemaakte presence reports — alles anders is een handmatige aanpassing
const isManualPresenceAdjustment = (doc) => doc?.Author && doc.Author !== 'system';
const isAddressVisitTask = (doc) => doc?.Type === 'addressVisit';

// Tabbladen zoals op de huishoudendetailpagina: één strip bovenaan, één blok
// eronder. Bewust wisselen in plaats van verbergen — elk blok haalt zijn eigen
// gegevens op en hoeft niet te draaien zolang niemand ernaar kijkt.

const ProjectDetails = () => {
  // Context
    const [auth] = useContext(Auth);

  // State
    const [activeTab, setActiveTab] = useState('status');
    const [updateError, setUpdateError] = useState(/** @type {string | null} */ (null));
    const [sessionOpen, setSessionOpen] = useState(false);
    const isManagementTenant = isSuperAdminTenant(client);
    const tabs = useMemo(() => projectDetailTabs(client), []);

  // Hooks
    const projectId = Location()[3]
    // Firestore
    const project = useFirestoreNoCompagny('CompagnyMeta', 'CompagnyID', projectId || '')

    // Retention scores voor dit ene project
    const projectMeta = Array.isArray(project) && project.length > 0 ? project[0] : null
    const projectLoading = !projectMeta
    const entitiesForScore = useMemo(() => (projectMeta ? [projectMeta] : []), [projectMeta])
    const { scoresByEntityId, loading: scoreLoading, unavailableCollections } = useRetentionScoresBulk(entitiesForScore)
    // Een geweigerde read geeft een lege lijst; zonder deze melding leest een
    // onvolledig beeld als een slecht scorend project.
    // AI-CONTEXT: See docs/inbox.md Bug #133
    const missingDataMessage = describeUnavailableData(unavailableCollections)
    const score = projectMeta ? scoresByEntityId.get(projectMeta.CompagnyID) : null
    // EventResults van een project leest een koepel niet zelf (de regels laten
    // alleen het eigen team toe); de kaart 'Activiteiten' krijgt ze van de server.
    const activityEntries = useProjectActivityEntries(projectMeta?.CompagnyID || '')
    // QuestionnairesResponses is team-only: ook deze via een Cloud Function.
    const researchEntries = useProjectResearchEntries(projectMeta?.CompagnyID || '')
    // Alleen opvragen als de kaart Taken er staat (huisbezoeken aan).
    const taskEntries = useProjectTaskEntries(isFlagOn(projectMeta?.HouseVisits) ? (projectMeta?.CompagnyID || '') : '')

    // De portfolio's waar dit project onder hangt. Parent bevat CompagnyID's;
    // de naam staat op het CompagnyMeta-document van de portfolio zelf.
    const parentIds = useMemo(
        () => (Array.isArray(projectMeta?.Parent) ? projectMeta.Parent.filter(Boolean) : []),
        [projectMeta]
    )
    const parentDocs = useFirestoreAllIn('CompagnyMeta', 'CompagnyID', parentIds)
    const parents = useMemo(
        () => (Array.isArray(parentDocs) ? parentDocs : [])
            .map((doc) => ({
                id: doc.CompagnyID,
                name: doc.CommunityName,
                to: `/${client}/portfolioprojectdetails/${doc.CompagnyID}`,
            }))
            .filter((parent) => parent.id && parent.name),
        [parentDocs]
    )
    // Het project noemt zijn portfolio's zelf, maar de namen komen uit een
    // tweede read. Zolang die loopt is 'geen portfolio' een onware bewering.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const parentsLoading = parentIds.length > 0 && parents.length === 0

    // Dezelfde tellingen als de kolommen in het projectenoverzicht. Ze lopen
    // via Cloud Functions omdat de beheertenant de TOC- en registratie-
    // collecties van andere organisaties niet via de Firestore-regels mag
    // lezen — en die functies zijn super-admin-gegate, dus een portfolio-
    // beheerder krijgt de kaarten niet. Beter geen kaart dan een kaart die
    // stil 0 toont.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const { countsByCompagnyId: tocByCompagnyId, error: tocError } =
        useOverviewCounts('getTocOverview', isManagementTenant)

    // Alleen sectoren met eigen registratiecollecties hebben hier iets te
    // tonen; zonder zo'n sector wordt de tweede serveraanroep niet gedaan.
    const registrationSectors = useMemo(
        () => sectorsWithRegistrations(projectMeta?.SelectedSectors),
        [projectMeta]
    )
    const { countsByCompagnyId: activityByCompagnyId, error: activityError } =
        useOverviewCounts('getSectorActivityOverview', isManagementTenant && registrationSectors.length > 0)

    const { selectableSectors } = useAvailableSectors()

    // Sector en status wijzigen loopt cross-tenant: de Firestore-regels laten
    // de beheertenant niet op CompagnyMeta van een ander project schrijven,
    // dus dit gaat via dezelfde super-admin-gegate functies als het overzicht.
    // Stabiele referentie, want ProjectSectorsCell is gememoiseerd.
    const updateSectors = useCallback(async (docId, sectors) => {
        try {
            const updateProjectSectors = firebase.app().functions('europe-west1').httpsCallable('updateProjectSectors')
            await updateProjectSectors({ docId, sectors })
            setUpdateError(null)
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to update sectors:', err)
            setUpdateError(`Bijwerken van de sector is mislukt: ${err.message}`)
        }
    }, [])

    const updateStatus = useCallback(async (status) => {
        const docId = projectMeta?.docid
        if (!docId) {
            setUpdateError('Bijwerken van status is mislukt: geen geldig document-ID gevonden.')
            return
        }
        try {
            const updateProjectStatus = firebase.app().functions('europe-west1').httpsCallable('updateProjectStatus')
            await updateProjectStatus({ docId, status })
            setUpdateError(null)
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to update status:', err)
            setUpdateError(`Bijwerken van status is mislukt: ${err.message}`)
        }
    }, [projectMeta])

    const host = window.location.hostname
    const projectUrl = projectMeta
        ? (host === 'localhost'
            ? `http://${host}:3001/${projectMeta.CompagnyID}/home`
            : `https://${host}/${projectMeta.CompagnyID}/home`)
        : null

    const lastLoginDays = projectMeta ? businessDaysSince(projectMeta.LastLogin) : null

    // Ben ik teamlid van deze omgeving? Zo niet, dan heeft een superadmin een
    // tijdelijke sessie nodig — anders is de omgeving leeg.
    // AI-CONTEXT: See docs/features/superadmin-tenant-access.md
    const myCompagnies = Array.isArray(auth?.Compagny) ? auth.Compagny : []
    const canStartSession = auth?.SuperAdmin === true
        && projectMeta
        && !myCompagnies.includes(projectMeta.CompagnyID)

  return (
    <div className="main">
        <div className="main-container">
            <div className="page-header project-detail-header">
                {projectMeta?.Logo
                    ? <img id="portfolio-project-details-logo" src={projectMeta.Logo} alt="" />
                    : <BusinessOutlinedIcon />}
                <h1>{projectMeta?.CommunityName || 'Project'}</h1>
            </div>

            {tabs.length > 1 && (
                <SelectableGrid
                    items={tabs}
                    getId={(item) => item.id}
                    getLabel={(item) => item.label}
                    selectedId={activeTab}
                    onClick={setActiveTab}
                />
            )}

            {activeTab === 'status' && (
              <div className="project-status-section">
                {missingDataMessage && (
                  <p className="project-status-incomplete" role="alert">{missingDataMessage}</p>
                )}
                {updateError && (
                  <p className="project-status-incomplete" role="alert">{updateError}</p>
                )}
                <ProjectMetaStrip
                  status={projectMeta?.Status}
                  daysSinceCreated={score?.signals?.daysSinceCreated ?? null}
                  lastLoginBusinessDaysAgo={lastLoginDays}
                  projectUrl={projectUrl}
                  loading={projectLoading}
                  scoreLoading={scoreLoading}
                  parents={parents}
                  parentsLoading={parentsLoading}
                  sectorsCell={projectMeta && (
                    <ProjectSectorsCell
                      project={projectMeta}
                      selectableSectors={selectableSectors}
                      canEdit={isManagementTenant}
                      onChange={updateSectors}
                    />
                  )}
                  onStatusChange={isManagementTenant ? updateStatus : null}
                  onStartSession={canStartSession ? () => setSessionOpen(true) : null}
                />

                <h2 className="project-status-heading">Gebruik</h2>
                <div className="project-status-entries">
                  <LastEntriesCard
                    label="Activiteiten"
                    collection="EventResults"
                    compagnyId={projectMeta?.CompagnyID}
                    noun="activiteit"
                    nounPlural="activiteiten"
                    loading={projectLoading}
                    source={activityEntries}
                  />
                  <LastEntriesCard
                    label="Onderzoeken"
                    collection="QuestionnairesResponses"
                    compagnyId={projectMeta?.CompagnyID}
                    noun="onderzoeksrespons"
                    nounPlural="onderzoeksresponsen"
                    loading={projectLoading}
                    source={researchEntries}
                  />
                  {isFlagOn(projectMeta?.Personas) && (
                    <LastEntriesCard
                      label="Aanwezigheid"
                      collection="PersonaPresenceReports"
                      compagnyId={projectMeta?.CompagnyID}
                      noun="aanpassing"
                      nounPlural="aanpassingen"
                      loading={projectLoading}
                      filter={isManualPresenceAdjustment}
                    />
                  )}
                  {isFlagOn(projectMeta?.HouseVisits) && (
                    <LastEntriesCard
                      label="Taken"
                      collection="Tasks"
                      compagnyId={projectMeta?.CompagnyID}
                      noun="taak"
                      nounPlural="taken"
                      loading={projectLoading}
                      filter={isAddressVisitTask}
                      source={taskEntries}
                    />
                  )}
                </div>

                {isManagementTenant && projectMeta && (
                  <ProjectMetricsCard
                    title="Theory of Change"
                    description="Hoeveel doelgroepen, activiteiten, mechanismen en effecten dit project heeft, en hoeveel effecten een compleet pad hebben."
                    metrics={TOC_METRICS}
                    byCompagnyId={tocByCompagnyId}
                    compagnyId={projectMeta.CompagnyID}
                    error={tocError}
                  />
                )}

                {isManagementTenant && projectMeta && registrationSectors.map((sector) => (
                  <ProjectMetricsCard
                    key={sector}
                    title={getSectorInfo(sector).label}
                    description="Registraties die bij deze sector horen, met tussen haakjes hoe lang geleden de laatste was."
                    metrics={sectorMetricsFor(sector)}
                    byCompagnyId={activityByCompagnyId}
                    compagnyId={projectMeta.CompagnyID}
                    error={activityError}
                    project={projectMeta}
                  />
                ))}

                <div className="project-status-decca-card">
                  <p className="project-status-decca-card__hint">
                    Vraag Decca om een analyse van de status van dit project.
                  </p>
                  <DeccaTriggerButton
                    subject="Status & gezondheid"
                    label="Analyseer met Decca"
                    contextData={score?.signals || ''}
                    tenantOverride={projectMeta?.CompagnyID || null}
                    tenantNameOverride={projectMeta?.CommunityName || null}
                    greeting={`<h2>Hallo ${auth?.ForName || ''}</h2><p>Zal ik de status en gezondheid van ${projectMeta?.CommunityName || 'dit project'} voor je analyseren?</p>`}
                  />
                </div>
              </div>
            )}

            {isManagementTenant && activeTab === 'tasks' && (
              <div className="project-tab-pane">
                <ProjectTasks projectId={projectId} projectName={projectMeta?.CommunityName || ''} />
              </div>
            )}

            {isManagementTenant && activeTab === 'notes' && (
              <div className="project-tab-pane">
                <ProjectNotes projectId={projectId} />
              </div>
            )}

            {/* De gate staat bewust twee keer: hier en in projectDetailTabs.
                Alleen de tabbladenlijst is niet genoeg — zie commit 40f9b4047.
                Let op: dit blok leest de KLANT-omgeving, de twee hierboven de
                beheeromgeving. AI-CONTEXT: docs/features/update-aanvragen.md */}
            {isManagementTenant && activeTab === 'requests' && (
              <div className="project-tab-pane">
                <ProjectUpdateRequests projectId={projectId} />
              </div>
            )}

            {sessionOpen && projectMeta && (
              <SuperAdminSessionModal
                modalOpen={true}
                setModalOpen={() => setSessionOpen(false)}
                compagnyId={projectMeta.CompagnyID}
                tenantName={projectMeta.CommunityName || projectMeta.CompagnyID}
                onStarted={() => {
                    window.open(projectUrl, '_blank', 'noreferrer')
                    setSessionOpen(false)
                }}
              />
            )}
        </div>
    </div>
  )
}

export default ProjectDetails
